import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { getDateName, getOrderDateTimeFromOloTimeWanted } from '@/utils/dateTimeUtils';

import { ApplicationConfigContext } from '@/components/common/ApplicationConfigProvider/ApplicationConfigProvider';
import { Arrow } from '@/components/common/Icons/Arrow';
import { BasketResponse } from '@/app/(ordering)/rewards/data/data.type';
import { Button } from '@/components/common/Button/Button';
import { CartContext } from '@/app/(ordering)/shop/CartProvider/CartProvider';
import { Clock } from '@/components/common/Icons/Clock';
import GoogleMap from '@/components/ordering/GoogleMap/GoogleMap';
import { Loading } from '@/components/common/Icons/Loading';
import { Location } from '@/components/common/Icons/Location';
import { PickupAddressProps } from '../../../CartProvider/cartProviderTypes';
import type { PickupCurrentView } from './PickupPanel';
import { StartOrderAddressCard } from '@/components/ordering/StartOrderAddressCard/StartOrderAddressCard';
import { sendGAEvent } from '@next/third-parties/google';
import type { shopDataLessProps } from '../../data/shopdata.type';
import styles from '../../../shop.module.css';

type PickupSelectionProps = {
	closeModal: Dispatch<SetStateAction<void>>;
	selectedShop: shopDataLessProps | null;
	setCurrentView: React.Dispatch<React.SetStateAction<PickupCurrentView>>;
	isLoadingPickupPanel: boolean;
};

export default function PickupSelection({
	closeModal,
	selectedShop,
	setCurrentView,
	isLoadingPickupPanel,
}: PickupSelectionProps) {
	const { setNewAddress, tempCart, apiCart, address, isFromCampaign, campaignName } =
		useContext(CartContext);
	const appConfigContext = useContext(ApplicationConfigContext);

	const [estimatedTime, setEstimatedTime] = useState<{
		displayTime: string;
		estTime: string;
	}>({
		displayTime: '',
		estTime: '',
	});
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [imageSrc, setImageSrc] = useState<string>();
	const [isLTOImageLoaded, setIsLTOImageLoaded] = useState<boolean>(false);

	useEffect(() => {
		if (selectedShop) {
			const cart: BasketResponse | undefined = tempCart ?? apiCart;
			if (!cart || !cart.formattedOrderTime) {
				return;
			}
			let estTime = cart.formattedOrderTime.split(' - ')[1];

			const timeArr = estTime.split(' ');
			if (timeArr[timeArr.length - 1] === 'am' || timeArr[timeArr.length - 1] === 'pm') {
				timeArr[timeArr.length - 1] = timeArr[timeArr.length - 1].toUpperCase();
			}
			estTime = timeArr.join(' ');

			const disTime = getOrderDateTimeFromOloTimeWanted(cart);
			setEstimatedTime({
				displayTime: `${getDateName(disTime.orderDate)}, ${disTime.orderTime}`,
				estTime: estTime,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedShop, apiCart]);

	const startPickupOrder = async (shop: shopDataLessProps) => {
		const shopAdd: PickupAddressProps = {
			shopId: shop.shopId,
			shopName: shop.shopName,
			streetAddress: shop.address1,
			city: shop.city,
			zipCode: shop.zipCode,
			latitude: shop.latitude,
			longitude: shop.longitude,
		};
		return await setNewAddress({
			type: 'pickup',
			deliveryAddress: null,
			pickupAddress: shopAdd,
			orderDateTime: address.orderDateTime,
		});
	};

	useEffect(() => {
		if (isFromCampaign && campaignName) {
			const base = `${appConfigContext?.FACEBOOK_LTO_IMAGE_URL}/${campaignName}`;
			const extensions = ['.png'];

			const loadImage = (url: string) =>
				new Promise<string>((resolve, reject) => {
					const img = new Image();
					img.onload = () => resolve(url);
					img.onerror = () => reject();
					img.src = url;
				});

			Promise.any(extensions.map((ext) => loadImage(`${base}${ext}`)))
				.then((validUrl) => {
					setImageSrc(validUrl);
					setIsLTOImageLoaded(true);
				})
				.catch(() => setIsLTOImageLoaded(false));
		}
	}, [isFromCampaign, campaignName, appConfigContext?.FACEBOOK_LTO_IMAGE_URL]);

	return (
		<>
			{selectedShop ? (
				<div className="grid gap-2">
					<GoogleMap
						locations={[
							{
								latitude: selectedShop.latitude,
								longitude: selectedShop.longitude,
								name: selectedShop.shopName,
							},
						]}
					/>
				</div>
			) : isLoadingPickupPanel ? (
				<div className="flex justify-center">
					<Loading size="large" fill="green" />
				</div>
			) : isFromCampaign && isLTOImageLoaded && imageSrc ? (
				<img
					className="aspect-[16/9] h-auto max-h-[275px] w-full rounded-[10px] object-center"
					src={imageSrc}
					alt="LTO image"
					height={44}
					width={500}
					fetchPriority="high"
				/>
			) : null}
			<div className="my-8 grid grid-rows-1 gap-2">
				<StartOrderAddressCard
					leftIcon={<Location className="icon" />}
					label="PICKUP LOCATION"
					title={selectedShop ? selectedShop.shopName : undefined}
					missingAddr={selectedShop ? undefined : 'Please Select An Address'}
					addressChildren={
						selectedShop && (
							<p
								className={`${styles.customIphonePadding} flex flex-col items-start text-sm`}
								data-type-style="body-3"
								data-ink="light"
							>
								{selectedShop.address1}
							</p>
						)
					}
					rightIcon={
						<Button
							variant="text"
							className="flex w-full justify-between"
							onClick={() => setCurrentView('searching')}
						>
							<Arrow className="icon ml-auto self-center" />
						</Button>
					}
					ctaAction={() => {
						setCurrentView('searching');
					}}
				/>
				<StartOrderAddressCard
					leftIcon={<Clock />}
					label="PICKUP TIME"
					title={selectedShop ? estimatedTime.displayTime : ''}
					addressChildren={
						<p className="text-sm" data-ink="light" data-type-style="body-3">
							Estimated Time: {selectedShop ? estimatedTime.estTime : ''}
						</p>
					}
					ctaAction={() => {
						if (selectedShop) {
							setCurrentView('dateTime');
						}
					}}
					rightIcon={
						<Button
							disabled={!selectedShop}
							variant="text"
							className="flex w-full justify-between"
							onClick={() => setCurrentView('dateTime')}
						>
							<Arrow className="icon ml-auto self-center" />
						</Button>
					}
					disabled={!selectedShop}
				/>
			</div>
			<p className="text-center text-sm" data-ink="light" data-type-style="body-3">
				{`Ordering More Than 6 Dozen? `}
				<a
					className="underline underline-offset-1"
					href={`https://www.ezcater.com/brand/pvt/krispy-kreme`}
					data-ink="link"
					target="_blank"
					rel="noreferrer"
				>
					{`Switch to Catering`}
				</a>
			</p>
			<div className="mx-auto grid w-full max-w-xs grid-cols-1 gap-6 py-8">
				<Button
					// data-type-style="button-mobile" // ! Inorder to make button text to be kept inline
					variant="primary"
					// size="large" // ! Inorder to make button text to be kept inline
					isLoading={isLoading}
					disabled={isLoading || !selectedShop}
					onClick={async () => {
						setIsLoading(true);
						// Track the Start pickup order ecommerce event (begin_checkout)
						if (typeof window !== 'undefined') {
							window.dataLayer = window.dataLayer || [];
							window.dataLayer.push({
								event: 'begin_checkout',
								eventModel: {
									type: 'button',
									action: 'click',
									location: 'pickup selection',
									text: 'Start pickup order',
									pathname: window.location.pathname,
									domain: window.location.hostname,
								},
							});
						}
						if (selectedShop) {
							await startPickupOrder(selectedShop);

							const eventData = {
								value: apiCart?.subtotal,
								currency: 'USD',
								tax: apiCart?.taxes.reduce((previousVal, taxItem) => taxItem.tax + previousVal, 0),
								shipping: apiCart?.deliveryFee,
								zipcode: apiCart?.restaurant.zip,
								storeNumber: apiCart?.storeNumber,
								shopCity: selectedShop.city,
								shopName: selectedShop.shopName,
							};
							sendGAEvent('event', 'pickup_order_started', { ...eventData });
							setIsLoading(false);
							closeModal();
						} else {
							setIsLoading(false);
						}
					}}
					className="!py-4 font-black uppercase tracking-[1px] md:px-4 md:py-8 md:text-lg md:font-semibold"
				>
					Start pickup order
				</Button>
			</div>
		</>
	);
}
